import { useScrollReveal } from '@/hooks/useScrollReveal';
import { useTranslation } from 'react-i18next';

const STAT_ICONS = ['ri-truck-line', 'ri-map-pin-2-line', 'ri-shield-check-line', 'ri-time-line'];
const STEP_ICONS = ['ri-calendar-check-line', 'ri-map-2-line', 'ri-tools-line', 'ri-rocket-line'];

export default function WhyChooseUs() {
  const statsRef = useScrollReveal<HTMLDivElement>();
  const headingRef = useScrollReveal<HTMLDivElement>();
  const stepsRef = useScrollReveal<HTMLDivElement>();
  const { t } = useTranslation();

  const stats = t('why.stats', { returnObjects: true }) as { value: string; label: string }[];
  const steps = t('why.steps', { returnObjects: true }) as { title: string; description: string }[];

  return (
    <section id="process" className="py-24 bg-teal-50">
      <div className="max-w-7xl mx-auto px-6">

        {/* ── Stats Bar ── */}
        <div
          ref={statsRef}
          className="reveal-on-scroll animate-fade-in-up grid grid-cols-2 lg:grid-cols-4 gap-6 mb-24"
        >
          {stats.map((s, i) => (
            <div
              key={s.label}
              className="bg-white rounded-2xl p-8 flex flex-col items-center text-center gap-3"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-teal-50 text-teal-500 text-2xl">
                <i className={STAT_ICONS[i]} />
              </div>
              <span className="text-4xl font-extrabold text-gray-900 leading-none">{s.value}</span>
              <span className="text-gray-500 text-sm font-medium">{s.label}</span>
            </div>
          ))}
        </div>

        {/* ── Section Header ── */}
        <div ref={headingRef} className="reveal-on-scroll animate-fade-in-up text-center mb-16">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-2 h-2 bg-teal-400 rounded-full" />
            <span className="text-gray-500 text-sm font-medium">{t('why.headingTag')}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
            {t('why.title1')}<br />
            <span className="text-teal-500">{t('why.title2')}</span>
          </h2>
          <p className="mt-5 text-gray-500 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
            {t('why.subtitle')}
          </p>
        </div>

        {/* ── Process Steps ── */}
        <div
          ref={stepsRef}
          className="reveal-on-scroll animate-fade-in-up grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {steps.map((step, i) => (
            <div key={i} className="relative flex flex-col gap-5">
              {/* Connector line (desktop only, not on last item) */}
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-8 left-[calc(100%-16px)] w-[calc(100%-48px)] h-px bg-teal-200 z-0" />
              )}

              <div className="relative z-10 bg-white rounded-2xl p-6 flex flex-col gap-4 h-full">
                {/* Step number + icon */}
                <div className="flex items-center justify-between">
                  <span className="text-5xl font-black text-teal-100 leading-none select-none">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-teal-500 text-white text-lg">
                    <i className={STEP_ICONS[i]} />
                  </div>
                </div>

                <h3 className="text-gray-900 font-bold text-base leading-snug">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed flex-1">{step.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── CTA ── */}
        <div className="mt-16 text-center">
          <p className="text-gray-500 text-sm mb-4">{t('why.ctaText')}</p>
          <button
            onClick={() => {
              const el = document.querySelector('#consultation');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white font-semibold text-sm px-8 py-4 rounded-full transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            {t('why.bookSession')}
            <i className="ri-arrow-right-line" />
          </button>
        </div>

      </div>
    </section>
  );
}